import React, { useEffect, useState } from 'react';
import { ArrowLeftOutlined, DownloadOutlined, EyeOutlined, FileOutlined, FileUnknownOutlined } from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    message,
    Modal,
    notification,
    Row,
    Skeleton,
    Tag,
    Typography,
    Tabs,
} from 'antd';
import api from '../../../utils/api';
import { Link, useHistory } from 'react-router-dom';
import { IParts, QuoteLineItemProps } from '../../../types/types';
import { EnhancedViewer as Viewer } from '@partsbadger/threedviewer';
import OrdersHistory from '../../../components/Parts/OrdersHistory';

import PartQuotes from '../../../components/Parts/PartQuotes';
import QuoteHistory from '../../../components/Parts/QuoteHistory';
import UserStore from '../../../stores/UserStore';
import SalesOrderStore from '../../../stores/SalesOrderStore';
import { ErrorRender } from '@partsbadger/library';

import PartStore from '../../../stores/PartStore';
import { observer } from 'mobx-react';
import PartEdit from '../PartEdit';
import QuoteStore from '../../../stores/QuoteStore';

import GroupListByPart from 'components/Parts/GroupListByPart';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

interface IPartDetailProps {
    match: {
        params: {
            id: number;
        };
    };
}

interface IPart extends IParts {
    contact: {
        fullname: string;
    };
}

import { ThreeDViewerModal } from 'components/ThreeDViewerModal';
import { PageHeader } from 'components/PageHeader';

const PartDetail: React.FC<IPartDetailProps> = props => {
    const [editPart, setEditPart] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<IPart>();

    const { id } = props.match.params;
    const [file2DUrl, set2DFile2DUrl] = useState<string | null>('');
    const [file3DUrl, set3DFile3DUrl] = useState<string | null>('');

    const history = useHistory();

    async function getPartDetails() {
        setLoading(true);
        await PartStore.getPartDetailsById(id)
            .then(response => {
                setData(response);

                setLoading(false);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'bottomRight',
                });

                setLoading(false);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    async function getGroupList() {
        setLoading(true);

        await PartStore.getGroupListByPartId(id)
            .then(response => {
                setLoading(false);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'topRight',
                });

                setLoading(false);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        getPartDetails();
        PartStore.getMaterials();
        PartStore.getFinishes();
        PartStore.getCoatings();
        getGroupList();
    }, []);

    const goCheckoutPage = async (
        order_items: {
            quantity: number;
            name: string;
            quote_has_product?: number;
            product_description: string;
            unit_price: string;
        }[]
    ) => {
        if (order_items.length == 0) {
            return message.error('No products were selected!');
        }

        if (!UserStore.user?.default_contact?.id) {
            return message.error('No contact provided');
        }

        const payload = {
            contact: UserStore.user.default_contact.id,
            line_items: order_items,
        };

        await SalesOrderStore.createOrderDraft(payload)
            .then(order => {
                history.push(`/my-account/orders/checkout/${order.id}/`);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'bottomRight',
                });

                setLoading(false);
            });
    };

    return (
        <>
            <PageHeader>
                <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                        history.push('/my-account/parts');
                    }}
                    className="text-primary m-0 p-0"
                >
                    Back to Parts List
                </Button>
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <Title level={1} className="mb-0 mr-2">
                        {data?.name}
                    </Title>
                    {data?.quote_line_items && data?.quote_line_items.length > 0 ? (
                        <PartQuotes id={id} part_data={data} />
                    ) : null}
                </div>
            </PageHeader>
            <div className="container">
                <Card loading={loading}>
                    <div className="d-flex flex-column flex-md-row align-items-center" style={{ columnGap: '2rem' }}>
                        <div>
                            {data?.step_file && data.step_file.image ? (
                                <div className="d-flex flex-column justify-content-center align-items-center">
                                    <img
                                        className="thumbnail-3d-file"
                                        style={{
                                            width: '200px',
                                            display: 'flex',
                                            margin: 'auto',
                                            justifyContent: 'center',
                                        }}
                                        src={data.step_file.image}
                                    />
                                </div>
                            ) : (
                                <FileUnknownOutlined
                                    style={{
                                        fontSize: 120,
                                        display: 'flex',
                                        margin: 'auto',
                                        justifyContent: 'center',
                                    }}
                                />
                            )}
                        </div>
                        <div className="w-100 flex-grow-1">
                            <>
                                <Descriptions layout="horizontal" bordered size="small" column={1}>
                                    <Descriptions.Item label={<Text strong>Name</Text>}>{data?.name}</Descriptions.Item>
                                    <Descriptions.Item label={<Text strong>Dimensions</Text>}>
                                        <span className="text-sm">
                                            {parseFloat(data?.length).toFixed(3)}
                                            {data?.unit} x {parseFloat(data?.width).toFixed(3)}
                                            {data?.unit} x {parseFloat(data?.height).toFixed(3)}
                                            {data?.unit}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text strong>Created By</Text>}>
                                        {`${data?.contact?.fullname}`}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text strong>3D File</Text>}>
                                        {data?.step_file?.filename ? (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="underline">{data?.step_file?.filename}</span>
                                                    <div>
                                                        <ThreeDViewerModal
                                                            url={data?.step_file?.file_3djs}
                                                            buttonStyles={{ maxWidth: '200px', textAlign: 'center' }}
                                                            partInfo={
                                                                data
                                                                    ? {
                                                                          name: data.name,
                                                                          dimensions: {
                                                                              length: String(data.length),
                                                                              width: String(data.width),
                                                                              height: String(data.height),
                                                                              unit: String(data.unit ?? 'MM'),
                                                                          },
                                                                      }
                                                                    : undefined
                                                            }
                                                        />
                                                        {/* <Button
                                                            onClick={() => {}}
                                                            disabled
                                                            icon={<DownloadOutlined />}
                                                            size="small"
                                                        ></Button> */}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            '--'
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text strong>2D File</Text>}>
                                        {data?.drawing_file?.file ? (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="underline">{data.drawing_file.filename}</span>
                                                    <div>
                                                        <Button
                                                            onClick={() => {
                                                                if (data?.drawing_file?.file) {
                                                                    set2DFile2DUrl(data.drawing_file.file);
                                                                }
                                                            }}
                                                            disabled={!data?.drawing_file?.file}
                                                            className="ant-btn-xs"
                                                        >
                                                            View Drawing
                                                        </Button>
                                                        {/* <Button
                                                            onClick={() => {}}
                                                            disabled
                                                            icon={<DownloadOutlined />}
                                                            size="middle"
                                                        ></Button> */}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            '--'
                                        )}
                                    </Descriptions.Item>
                                </Descriptions>
                            </>
                        </div>
                    </div>
                </Card>
                <Card>
                    <Tabs defaultActiveKey="quotes">
                        <TabPane
                            tab={
                                <div className="font-bold text-md">
                                    Quote History ({data?.quote_line_items.length ?? 0})
                                </div>
                            }
                            key="quotes"
                        >
                            <QuoteHistory
                                master_product_id={id}
                                part_data={data}
                                goCheckoutPage={goCheckoutPage}
                                editPart={(quote_line_item: QuoteLineItemProps) => {
                                    QuoteStore.set_line_item(quote_line_item);
                                    setEditPart(true);
                                }}
                            />
                        </TabPane>
                        <TabPane
                            tab={
                                <div className="font-bold text-md">
                                    Order History ({data?.sales_order_line_items.length ?? 0})
                                </div>
                            }
                            key="orders"
                        >
                            <OrdersHistory master_product_id={id} part_data={data} />
                        </TabPane>
                        <TabPane
                            tab={
                                <div className="font-bold text-md">
                                    Group List ({PartStore?.allGroupsAssignedToThePart?.length ?? 0})
                                </div>
                            }
                            key="grouplist"
                        >
                            <GroupListByPart master_product_id={id} />
                        </TabPane>
                    </Tabs>
                </Card>

                {file2DUrl && (
                    <Drawer open width={800} onClose={() => set2DFile2DUrl(null)} placement={'left'}>
                        <embed src={file2DUrl} type="application/pdf" width="100%" height={`800px`} />
                    </Drawer>
                )}
            </div>

            <Modal
                title={
                    <>
                        <span className={'text-lg font-bold'}>{data?.name}</span>
                    </>
                }
                open={editPart}
                width="78vw"
                footer={false}
                closable={true}
                onCancel={() => {
                    QuoteStore.set_line_item();
                    setEditPart(false);
                }}
                bodyStyle={{ overflowY: 'scroll', overflowX: 'auto' }}
            >
                <PartEdit
                    setEditPart={(value: boolean, getParts: boolean) => {
                        if (getParts) getPartDetails();
                        setEditPart(value);
                    }}
                    goCheckoutPage={goCheckoutPage}
                />
            </Modal>
        </>
    );
};

export default observer(PartDetail);
